import type { FC, ReactNode } from 'react'
import type { Project } from '@/types'

import { cn } from '@/lib/utils'

interface WorkArtProps {
    project: Project
    className?: string
}

const tones = {
    blaze: { surface: 'bg-blaze', ink: 'text-ink', block: 'bg-ink', soft: 'bg-ink/15', line: 'bg-ink/25' },
    cobalt: { surface: 'bg-cobalt', ink: 'text-ink', block: 'bg-ink', soft: 'bg-ink/15', line: 'bg-ink/25' },
    ink: { surface: 'bg-[#2a241e]', ink: 'text-paper', block: 'bg-blaze', soft: 'bg-paper/12', line: 'bg-paper/20' },
    paper: { surface: 'bg-paper-deep', ink: 'text-ink', block: 'bg-cobalt', soft: 'bg-ink/10', line: 'bg-ink/20' }
}

// A browser-framed preview of a client site. Shows the real screenshot once one is set on the project,
// and a typeset stand-in with the client's name until then.
const WorkArt: FC<WorkArtProps> = ({ project, className }): ReactNode => {
    const tone = tones[project.tone]

    return (
        <div className={cn('overflow-hidden rounded-lg border-2 border-ink bg-paper', className)}>
            <div aria-hidden='true' className='flex h-8 items-center gap-1.5 border-b-2 border-ink bg-paper px-3 sm:h-9'>
                <span className='size-2.5 rounded-full bg-blaze' />
                <span className='size-2.5 rounded-full bg-ink/25' />
                <span className='size-2.5 rounded-full bg-ink/25' />
                <span className='ml-3 h-4 w-2/5 max-w-56 rounded-full bg-ink/10' />
            </div>

            <div className={cn('relative aspect-[16/10] overflow-hidden', tone.surface)}>
                {project.image ? (
                    <img
                        src={project.image}
                        alt={`${project.name} website`}
                        loading='lazy'
                        className='absolute inset-0 size-full object-cover object-top'
                    />
                ) : (
                    <div role='img' aria-label={`${project.name} website preview`} className={cn('absolute inset-0 flex flex-col p-[6%]', tone.ink)}>
                        <div aria-hidden='true' className='flex items-center justify-between'>
                            <span className={cn('h-[0.55rem] w-[18%] rounded-full', tone.block)} />

                            <span className='flex gap-[0.6rem]'>
                                <span className={cn('h-[0.45rem] w-8 rounded-full sm:w-10', tone.line)} />
                                <span className={cn('h-[0.45rem] w-8 rounded-full sm:w-10', tone.line)} />
                                <span className={cn('hidden h-[0.45rem] w-10 rounded-full sm:block', tone.line)} />
                            </span>
                        </div>

                        <div className='mt-auto'>
                            <p className='font-heading text-[clamp(1.75rem,6vw,4.5rem)] font-extrabold leading-[0.9] tracking-[-0.04em] wdth-78 lg:text-[clamp(2rem,3.6vw,4.5rem)]'>
                                {project.name}
                            </p>

                            <div aria-hidden='true' className='mt-[5%] flex items-center gap-3'>
                                <span className={cn('h-[1.6rem] w-[22%] rounded-md sm:h-8', tone.block)} />
                                <span className={cn('h-[0.45rem] w-[30%] rounded-full', tone.line)} />
                            </div>
                        </div>

                        <div aria-hidden='true' className='mt-[6%] grid grid-cols-3 gap-[3%]'>
                            <span className={cn('aspect-[2/1] rounded-md', tone.soft)} />
                            <span className={cn('aspect-[2/1] rounded-md', tone.soft)} />
                            <span className={cn('aspect-[2/1] rounded-md', tone.soft)} />
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default WorkArt
